import { Injectable } from '@angular/core';
import { AuthService as Auth0AngularService } from '@auth0/auth0-angular';
import { createAuth0Client, Auth0Client } from '@auth0/auth0-spa-js';
import { AuthTenant } from '../enums/auth-tenant.enum';
import { Auth0ConfigService } from './auth0-config.service';
import { AuthTenantContextService } from './auth-tenant-context.service';
import { Observable, firstValueFrom } from 'rxjs';

export interface IAuth0Provider {
    loginWithRedirect(options?: any): Promise<void>;
    handleRedirectCallback(): Promise<any>;
    getIdTokenClaims(): Promise<any>;
    logout(options?: any): Promise<void> | void;
    isAuthenticated$?: Observable<boolean>;
    idTokenClaims$?: Observable<any>;
    error$?: Observable<Error>;
}

@Injectable({
    providedIn: 'root'
})
export class Auth0ProviderFactory {
    private clients: Map<AuthTenant, Auth0Client> = new Map();
    private clientPromises: Map<AuthTenant, Promise<Auth0Client>> = new Map();

    // Store captured callback parameters to prevent them from being lost
    private capturedCallbackUrl: string | null = null;

    constructor(
        private auth0ConfigService: Auth0ConfigService,
        private authTenantContext: AuthTenantContextService,
        private auth0AngularService: Auth0AngularService
    ) {
        // Capture callback URL immediately when the service is instantiated
        this.captureCallbackUrl();
    }

    private captureCallbackUrl(): void {
        if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
            this.capturedCallbackUrl = window.location.href;
        }
    }

    async getProvider(tenant?: AuthTenant): Promise<IAuth0Provider> {
        let selectedTenant = tenant || this.authTenantContext.getSelectedTenant();

        // Check for callback BEFORE the URL gets modified by Angular router
        if (!tenant && window.location.search.includes('code=')
            && window.location.search.includes('state=')) {
            if (this.authTenantContext.getSelectedTenant() === AuthTenant.Secondary) {
                selectedTenant = AuthTenant.Secondary;
            }
        }

        if (selectedTenant === AuthTenant.Primary) {
            return this.createAngularServiceWrapper();
        } else {
            const client = await this.getOrCreateSpaClient(selectedTenant);
            return this.createSpaClientWrapper(client);
        }
    }

    private createAngularServiceWrapper(): IAuth0Provider {
        return {
            loginWithRedirect: (options?: any) => firstValueFrom(this.auth0AngularService.loginWithRedirect(options)),
            handleRedirectCallback: async () => {
                await firstValueFrom(this.auth0AngularService.handleRedirectCallback());
                const isAuthenticated = await firstValueFrom(this.auth0AngularService.isAuthenticated$);
                if (!isAuthenticated) {
                    throw new Error('Authentication did not complete successfully after callback');
                }
                return Promise.resolve();
            },
            getIdTokenClaims: () => firstValueFrom(this.auth0AngularService.idTokenClaims$),
            logout: (options?: any) => firstValueFrom(this.auth0AngularService.logout(options)),
            isAuthenticated$: this.auth0AngularService.isAuthenticated$,
            idTokenClaims$: this.auth0AngularService.idTokenClaims$,
            error$: this.auth0AngularService.error$
        };
    }

    private createSpaClientWrapper(client: Auth0Client): IAuth0Provider {
        return {
            loginWithRedirect: (options?: any) => {
                const optionsWithAppState = {
                    ...options,
                    appState: {
                        ...options?.appState,
                        authTenant: AuthTenant.Secondary
                    }
                };
                return client.loginWithRedirect(optionsWithAppState);
            },
            handleRedirectCallback: async () => {
                if (this.capturedCallbackUrl) {
                    const url = new URL(this.capturedCallbackUrl);
                    const urlParams = new URLSearchParams(url.search);
                    const code = urlParams.get('code');
                    const state = urlParams.get('state');

                    if (code && state) {
                        try {
                            return await client.handleRedirectCallback(this.capturedCallbackUrl);
                        } finally {
                            this.capturedCallbackUrl = null;
                        }
                    } else {
                        this.capturedCallbackUrl = null;
                        throw new Error('Captured URL missing code or state parameters');
                    }
                } else {
                    const currentUrl = new URL(window.location.href);
                    const urlParams = new URLSearchParams(currentUrl.search);
                    const code = urlParams.get('code');
                    const state = urlParams.get('state');

                    if (code && state) {
                        return client.handleRedirectCallback();
                    } else {
                        throw new Error('There are no query params available for parsing.');
                    }
                }
            },
            getIdTokenClaims: () => client.getIdTokenClaims(),
            logout: (options?: any) => client.logout(options),
            isAuthenticated$: undefined,
            idTokenClaims$: undefined,
            error$: undefined
        };
    }

    private async getOrCreateSpaClient(tenant: AuthTenant): Promise<Auth0Client> {
        if (this.clients.has(tenant)) {
            return this.clients.get(tenant)!;
        }

        if (this.clientPromises.has(tenant)) {
            return this.clientPromises.get(tenant)!;
        }

        const config = this.auth0ConfigService.getTenantConfig(tenant);
        const options = {
            domain: this.stripHttpsPrefix(config.authUri),
            clientId: config.clientId,
            authorizationParams: {
                redirect_uri: config.redirectUri
            }
        };

        const clientPromise = createAuth0Client(options).then(client => {
            this.clients.set(tenant, client);
            this.clientPromises.delete(tenant);
            return client;
        });

        this.clientPromises.set(tenant, clientPromise);
        return clientPromise;
    }

    private stripHttpsPrefix(uri: string): string {
        return uri?.startsWith('https://') ? uri.substring(8) : uri;
    }

    clearClients(): void {
        this.clients.clear();
        this.clientPromises.clear();
    }
}
